<template>
  <div class="status-row">
    <template v-if="!deleted">
      <div
        class="status"
        v-if="(!editMode || (editMode && isBase) || (editMode && !isBase && isHide)) && !isNew"
        @mousemove="hideSavedMessage"
      >
        <span class="name">{{ name }}</span>

        <div class="right-part" v-if="!editMode || isHide">
          <transition name="fade">
            <div class="saved" v-show="saved && !isUpdating">
              <i class="icon icon-done"></i>
              <span>{{ $tr('settings.statuses.saved') }}</span>
            </div>
          </transition>

          <div class="actions">
            <vbutton class="group"
              color="transparent"
              type="icon"
              icon="icon_edit"
              @click="enableEditMode" />

            <vbutton class="group"
              color="transparent"
              type="icon"
              icon="icon-bin"
              :disabled="model.has_responses"
              v-tooltip="{
                content: $tr('settings.statuses.statusWithCandidates'),
                placement: 'top-center',
                classes: [canNotDelete, 'width160']
              }"
              @click="!isBase ? deleteThisStatus() : toggleDeleteStatus(true)" />
          </div>
        </div>

      </div>

      <edit-row
        class="edit-row"
        v-if="(editMode || isNew) && !isHide"
        :initialName="name"
        :model="model"
        :isNew="isNew"
        :isBase="isBase"
        :templates="templates"
        :templatesNames="templatesNames"
        :callback-with-name="editTitle"
        :language="language"
        :errorMessage="errorMessage"
        @close="hideEditMode"
        @recover="recover"
        @save="save"
      />
    </template>

    <deleted-row
      v-else
      :status="model"
      @undoFinallyDelete="undoFinallyDelete"
      @finallyDelete="finallyDelete"
    />

    <modal-remove
      v-if="isHandleDeleteStatus"
      @remove="deleteThisStatus"
      @cancel="toggleDeleteStatus(false)"
    />
  </div>
</template>

<script>
import EditRow from './AddRow.vue'
import modalRemove from './ModalRemove.vue'

import { getUniqueName } from '@/_helpers/utils.js'

export default {
  name: 'settings-status',

  components: {
    EditRow,
    modalRemove
  },

  props: {
    index: Number,
    model: Object,
    templates: Array,
    haveSpaceInTheStatusBar: Boolean,
    tooFewStatuses: Boolean,
    isNew: Boolean,
    isClosed: Object,
    language: Object,
    vacancyId: String
  },

  methods: {
    editTitle (title) {
      this.updateModel({ title: title })
    },

    save (model) {
      this.errorMessage = ''
      const initialTitleTrimmed = this.initialTitle ? this.initialTitle.trim() : ''
      const modelTitleTrimmed = model.title ? model.title.trim() : ''
      const title = initialTitleTrimmed === modelTitleTrimmed ? initialTitleTrimmed : getUniqueName(this.templatesNames, modelTitleTrimmed)
      const modelWithUniqueTitle = Object.assign({}, model, {title})

      if (!this.isNew) {
        this.updateModel(modelWithUniqueTitle)
        return
      }
      this.$store.dispatch('vacancy/createTemplateDefault', modelWithUniqueTitle).then(() => {
        this.$store.dispatch('vacancy/getEmailTemplatesDefault').catch(err => {
          if(err.response === undefined) {
            this.$noty.error(this.$tr('common.error.networkError'));
          }
        })
        this.hideEditMode()
      }).catch(err => {
        if(err.response === undefined) {
          this.$noty.error(this.$tr('common.error.networkError'));
        } else {
          this.errorMessage = err.message
        }
      })
    },

    recover (model) {
      this.$store.dispatch('vacancy/recoverVacancyTemplate', {
        vacancyId: this.vacancyId,
        templateIds: [+model.id]
      }).then(() => {
        this.$store.dispatch('vacancy/getEmailTemplatesAll', {
          id: this.vacancyId
        })
        this.hideEditMode()
      })
    },

    updateModel (model) {
      // const model = {
      //   id: this.vacancyId,
      //   model: data
      // }
      this.$store.dispatch('vacancy/updateEmailTemplateDefault', model).then(() => {
        this.$store.dispatch('vacancy/getEmailTemplatesDefault')
        .catch(err => {
          if(err.response === undefined) {
            this.$noty.error(this.$tr('common.error.networkError'));
          }
        })
        this.hideEditMode()
      }).catch(err => {
        if(err.response === undefined) {
          this.$noty.error(this.$tr('common.error.networkError'));
        }
      })
    },

    deleteThisStatus () {
      this.$store.dispatch('vacancy/deleteEmailTemplateDefault', {
        id: this.model.id
      }).then(() => {
        this.$store.dispatch('vacancy/getEmailTemplatesDefault')
        this.toggleDeleteStatus(false);
      }).catch(err => {
        if(err.response === undefined) {
          this.$noty.error(this.$tr('common.error.networkError'));
        }
        this.toggleDeleteStatus(false);
      })
    },

    toggleDeleteStatus(val) {
      this.isHandleDeleteStatus = val;
    },

    undoFinallyDelete () {
      clearTimeout(this.finallyDeleteTimeout)
      this.$store.dispatch('dictionary/undoFinallyDelete', {
        id: this.model.id
      })
    },

    finallyDelete () {
      clearTimeout(this.finallyDeleteTimeout)
      this.$store.dispatch('vacancy/deleteEmailTemplate', {
        id: this.model.id,
        model: this.vacancyId
      })
    },

    toggleEditMode () {
      this.editMode = !this.editMode
      this.$emit('close')
    },

    enableEditMode () {
      this.editMode = true
      this.$emit('close')
    },

    hideEditMode () {
      this.editMode = false
      this.$emit('cancel')
    },

    hideSavedMessage () {
      clearTimeout(this.timeoutSaved)
      this.saved = false
    },

    setInitialTitle () {
      if (!this.model) {
        return
      }

      this.initialTitle = this.model.title
    },

    getTemplateTitle (template) {
      if (template.title) {
        return template.title
      } else {
        return template.response_status ? template.response_status.title : ''
      }
    }
  },

  computed: {
    isHide () {
      if (!this.isClosed) {
        return false
      }

      return this.isClosed.isClosed
    },

    isBase () {
      if (!this.model) {
        return false
      }

      return this.model.is_standard
    },

    canNotDelete () {
      return this.model.has_responses ? '' : 'hide'
    },

    deleted () {
      return this.model.deleted
    },

    templatesNames () {
      if (!this.templates) {
        return []
      }

      return this.templates
        .map(template => this.getTemplateTitle(template))
        .filter(name => name !== this.getTemplateTitle(this.model))
    },

    updatingStatusesLocalIds () {
      return this.$store.getters['dictionary/updatingStatusesLocalIds']
    },

    name: {
      get () {
        return this.model.title || (this.model && this.model.response_status ? this.model.response_status.title : '')
      },

      set (val) {
        this.updateModel({ title: val })
      }
    }
  },

  data () {
    return {
      editMode: false,
      saved: false,
      tempStatuses: [],
      initialTitle: '',
      errorMessage: '',
      isHandleDeleteStatus: false
    }
  },

  beforeMount () {
    this.setInitialTitle()
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

.status-row {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  flex-direction: column;
  width: 640px;

  &:first-child .status {
    border-top: 1px solid #dddddd;
  }
}

.status {
  display: flex;
  align-items: center;
  padding: 20px 20px 20px 10px;
  box-sizing: border-box;
  width: 100%;
  border-bottom: 1px solid #dddddd;
  position: relative;

  &:hover {
    .lock,
    .actions {
      display: flex;
    }
  }

  &.updating {
    pointer-events: none;

    > *:not(.saving) {
      opacity: 0.3;
    }
  }

  .status-switch {
    margin-right: 25px;
  }

  .name {
    font-size: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 5px;
  }

  .right-part {
    margin-left: auto;
    display: flex;
    align-items: center;
  }

  .lock,
  .actions {
    display: flex;
    align-items: center;
  }

  .lock {
    &:hover {
      cursor: default;
    }

    .icon-lock {
      width: 14px;
      height: 18px;
      background: url(/static/images/elements/lock.svg) center no-repeat;
      background-size: contain;
      margin-right: 8px;
    }

    span {
      color: #7f7f7f;
      font-size: 16px;
      font-weight: 500;
    }
  }

  .actions {
    .group + .group {
      margin-left: 22px;
    }

    .group {
      display: flex;
      align-items: center;

      &:hover {
        cursor: pointer;
      }

      &.disable {
        cursor: not-allowed;
      }
    }

    .icon {
      color: $green;
      margin-right: 7.5px;
    }

    span {
      color: $green;
      font-size: 16px;
      font-weight: 500;
    }
  }

  .saving {
    width: 18px;
    height: 18px;
    position: absolute;
    right: 20px;

    &:before {
      content: '';
      box-sizing: border-box;
      position: absolute;
      top: 50%;
      left: 50%;
      width: 18px;
      height: 18px;
      margin-top: -9px;
      margin-left: -9px;
      border-radius: 50%;
      border: 3px solid transparent;
      border-top-color: #7f7f7f;
      border-left-color: #7f7f7f;
      border-bottom-color: #7f7f7f;
      animation: spinner 0.6s linear infinite;
    }
  }

  .saved {
    display: flex;
    align-items: center;

    .icon-done {
      color: #7f7f7f;
      font-size: 10px;
      margin-right: 3px;
    }

    + .actions,
    + .lock {
      margin-left: 10px;
    }
  }
}

.edit-row {
  padding-bottom: 20px;
  border-bottom: 1px solid #dddddd;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
